import React from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import {PT_KPIS, PT_PERIOD, PT_POLICY} from "../paymentTermsDemoData";
import {usePaymentTermsTrendQuery} from "../usePaymentTermsQueries";

const W = 920;
const H = 300;
const PAD = {top: 24, right: 96, bottom: 34, left: 44};
const LINE_COLOR = '#478FFA';
const INK = '#44474E';
const GRID = 'rgba(0, 0, 0, 0.07)';

/**
 * Month-by-month spend-weighted average payment term — the progress view.
 * Single series with the policy target as a dashed reference line.
 */
export const TrendView: React.FC = () => {
    const [hover, setHover] = React.useState<number | null>(null);
    const svgRef = React.useRef<SVGSVGElement>(null);
    const {data: points = [], isPending} = usePaymentTermsTrendQuery();

    const target = PT_POLICY.targetTermDays;

    if (isPending) {
        return <Box className="pt-trend" display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28}/>
        </Box>;
    }

    if (points.length === 0) {
        return <Box className="pt-trend">
            <Box className="pt-card">
                <Typography variant="body2" color="text.secondary">No trend data available.</Typography>
            </Box>
        </Box>;
    }

    const yMin = Math.floor((Math.min(...points.map(p => p.value)) - 2) / 5) * 5;
    const yMax = Math.ceil((target + 2) / 5) * 5;
    const denom = Math.max(1, points.length - 1);
    const x = (i: number) => PAD.left + (i / denom) * (W - PAD.left - PAD.right);
    const y = (v: number) => PAD.top + (1 - (v - yMin) / (yMax - yMin)) * (H - PAD.top - PAD.bottom);

    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
    const area = `${path} L${x(points.length - 1).toFixed(1)},${y(yMin).toFixed(1)} L${x(0).toFixed(1)},${y(yMin).toFixed(1)} Z`;

    const gridLines: number[] = [];
    for (let v = yMin; v <= yMax; v += 5) gridLines.push(v);

    const first = points[0];
    const last = points[points.length - 1];
    const delta = last.value - first.value;

    const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;
        const px = ((e.clientX - rect.left) / rect.width) * W;
        const i = Math.round(((px - PAD.left) / (W - PAD.left - PAD.right)) * denom);
        setHover(Math.max(0, Math.min(points.length - 1, i)));
    };

    return <Box className="pt-trend">
        <Box className="pt-card">
            <Box className="pt-card-header">
                <Typography variant="h2" className="pt-card-title">
                    Weighted average payment term — monthly trend
                </Typography>
                <span className="pt-card-hint">
                    {PT_PERIOD.from} – {PT_PERIOD.to} · spend-weighted, all suppliers · demo series anchored to the period-end value
                </span>
            </Box>

            <Box className="pt-trend-summary">
                <span className="pt-trend-delta" style={{color: delta >= 0 ? '#08A57A' : '#FF3435'}}>
                    {delta >= 0 ? '+' : ''}{delta.toFixed(1)} days
                </span>
                <span className="pt-trend-delta-label">
                    over the last 12 months — {(target - last.value).toFixed(1)} days still to go to the {target}-day policy target
                </span>
            </Box>

            <Box className="pt-trend-chart">
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${W} ${H}`}
                    role="img"
                    aria-label={`Weighted average payment term per month, from ${first.value} days in ${first.month} to ${last.value} days in ${last.month}`}
                    onMouseMove={onMove}
                    onMouseLeave={() => setHover(null)}>

                    {gridLines.map(v => (
                        <g key={v}>
                            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth={1}/>
                            <text x={PAD.left - 8} y={y(v) + 3.5} textAnchor="end" fontSize={11} fill={INK}>{v}d</text>
                        </g>
                    ))}

                    {/* Policy target reference */}
                    <line x1={PAD.left} x2={W - PAD.right} y1={y(target)} y2={y(target)}
                          stroke={INK} strokeWidth={1.5} strokeDasharray="6 4" opacity={0.55}/>
                    <text x={W - PAD.right + 8} y={y(target) + 3.5} fontSize={11.5} fontWeight={700} fill={INK}>
                        Target {target}d
                    </text>

                    <path d={area} fill={LINE_COLOR} opacity={0.07}/>
                    <path d={path} fill="none" stroke={LINE_COLOR} strokeWidth={2} strokeLinejoin="round"/>

                    {/* Direct labels on the endpoints only */}
                    <text x={x(0)} y={y(first.value) - 10} textAnchor="middle" fontSize={11.5} fontWeight={700} fill={INK}>
                        {first.value.toFixed(1)}d
                    </text>
                    <text x={x(points.length - 1)} y={y(last.value) - 10} textAnchor="middle" fontSize={11.5} fontWeight={700} fill={INK}>
                        {last.value.toFixed(1)}d
                    </text>

                    {points.map((p, i) => (
                        <text key={p.month} x={x(i)} y={H - 10} textAnchor="middle" fontSize={10.5} fill={INK} opacity={0.75}>
                            {p.month}
                        </text>
                    ))}

                    {/* Hover crosshair + tooltip */}
                    {hover !== null && <>
                        <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom}
                              stroke={INK} strokeWidth={1} opacity={0.25}/>
                        <circle cx={x(hover)} cy={y(points[hover].value)} r={4.5}
                                fill={LINE_COLOR} stroke="#fff" strokeWidth={2}/>
                        <g transform={`translate(${Math.min(x(hover) + 10, W - PAD.right - 118)}, ${Math.max(y(points[hover].value) - 40, 8)})`}>
                            <rect width={112} height={34} rx={6} fill="#fff" stroke="rgba(0,0,0,0.15)"/>
                            <text x={10} y={14} fontSize={10.5} fill={INK} opacity={0.8}>{points[hover].month}</text>
                            <text x={10} y={27} fontSize={12} fontWeight={700} fill={INK}>
                                {points[hover].value.toFixed(1)} days
                            </text>
                        </g>
                    </>}

                    {points.map((p, i) => (
                        <circle key={p.month} cx={x(i)} cy={y(p.value)} r={2.5} fill={LINE_COLOR}/>
                    ))}
                </svg>
            </Box>

            <Typography className="pt-bucket-footnote">
                The weighted average term improved {delta.toFixed(1)} days over the period but remains below the
                {' '}{target}-day policy. Month-by-month movement tracks negotiation progress: harmonisation quick wins
                ({formatDelta(PT_KPIS.quickWinOpportunity)}) move the line first; policy renegotiations close the rest.
                A category-level split of this trend is planned once category data is connected.
            </Typography>
        </Box>
    </Box>;
};

function formatDelta(v: number): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency: 'EUR', notation: 'compact', maximumFractionDigits: 1,
        }).format(v);
    } catch {
        return `EUR ${Math.round(v).toLocaleString('en-US')}`;
    }
}
